import type BoundsUtil from "./boundsUtil";
import type DistanceUnitUtil from "./distanceUnitUtil";
import type ObjectUtil from "./objectUtil";
import type { AndCriteria, Criteria, DateGreaterCriteria, DateGreaterEqualCriteria, DateLessEqualCriteria, EqualCriteria, GreaterCriteria, GreaterEqualCriteria, InCriteria, InteractsCriteria, LessCriteria, LessEqualCriteria, LikeCriteria, NearestCriteria, NotCriteria, NotEqualCriteria, OrCriteria, TerritoryLevelCriteria, WithinDistanceCriteria } from "@esWebAPI/es/model/Criteria";

const es = global.es; // TODO: Remove dependency

export default class CriteriaUtil {
    private boundsUtil: BoundsUtil;
    private distanceUnitUtil: DistanceUnitUtil;
    private objectUtil: ObjectUtil;
    private matchers: CriteriaUtilMatchers;

    constructor(boundsUtil: BoundsUtil, distanceUnitUtil: DistanceUnitUtil, objectUtil: ObjectUtil) {
        this.boundsUtil = boundsUtil;
        this.distanceUnitUtil = distanceUnitUtil;
        this.objectUtil = objectUtil;

        this.matchers = new CriteriaUtilMatchers(this);
    }

    /**
     * 
     */
    and(...args: Criteria[]): Criteria | null {
        const results: Criteria[] = [];
        for (let i = 0; i < args.length; i++) {
            flatten(args[i], results, true);
        }

        for (let i = results.length; i-- > 0;) {
            const crit = results[i];

            if (crit.$type === "None") {
                return crit;
            } else if(crit.$type === "All") {
                if (results.length === 1) {
                    return crit;
                } else {
                    results.splice(i, 1); // An All combined with other components is meaningless - remove it
                }
            } else {
                for (let j = i; j-- > 0;) {
                    if ((results[i] === results[j]) || (this.objectUtil.equals(results[i], results[j]))) {
                        results.splice(i, 1);
                        break;
                    }
                }
            }
        }

        if (results.length === 1) {
            return results[0];
        } else if (results.length) {
            return {
                $type: "And",
                criteria: results
            };
        } else {
            return null;
        }
    }

    /**
     * 
     */
    unflattenAnd(...args: Criteria[]): Criteria | null {
        const results = args.filter(c => c !== undefined);

        for (let i = results.length; i-- > 0;) {
            const crit = results[i];

            if (crit.$type === "None") {
                return crit;
            } else if(crit.$type === "All") {
                if (results.length === 1) {
                    return crit;
                } else {
                    results.splice(i, 1); // An All combined with other components is meaningless - remove it
                }
            } else {
                for (let j = i; j-- > 0;) {
                    if((results[i] === results[j]) || (this.objectUtil.equals(results[i], results[j]))){
                        results.splice(i, 1);
                        break;
                    }
                }
            }
        }

        if (results.length === 1) {
            return results[0];
        } else if (results.length) {
            return {
                $type: "And",
                criteria: results
            };
        } else {
            return null;
        }
    }

    /**
     * 
     */
    or(...args: Criteria[]): Criteria | null {
        const results: Criteria[] = [];
        for (let i = 0; i < args.length; i++) {
            flatten(args[i], results, false);
        }

        for (let i = results.length; i-- > 0;) {
            const crit = results[i];

            if (crit.$type === "None") {
                if (results.length === 1) {
                    return crit;
                } else {
                    results.splice(i, 1); // An None combined with other components is meaningless - remove it
                }
            } else if (crit.$type === "All") {
                return crit; // an All combined with other, means All
            } else {
                for (let j = i; j-- > 0;) {
                    if ((results[i] === results[j]) || (this.objectUtil.equals(results[i], results[j]))) {
                        results.splice(i, 1);
                        break;
                    }
                }
            }
        }

        if (results.length === 1) {
            return results[0];
        } else if (results.length) {
            return {
                $type: "Or",
                criteria: results
            };
        } else {
            return null;
        }
    }

    /**
     * 
     * @param criteria 
     * @param partialCriteria 
     * 
     * @returns 
     */
    contains(criteria: Criteria | null, partialCriteria: object): boolean {
        if (!criteria) {
            return false;
        }

        if (criteria.$type === "And" || criteria.$type === "Or") {
            for (let i = 0; i < criteria.criteria.length; i++) {
                if (this.contains(criteria.criteria[i], partialCriteria)) {
                    return true;
                }
            }

            return false;
        }

        return matchesPartial(criteria, partialCriteria);
    }

    /**
     * 
     * @param criteria 
     * @param partialCriteria 
     * 
     * @returns 
     */
    remove(criteria: Criteria | null, partialCriteria: Object): Criteria | null {
        if (!criteria) {
            return criteria;
        }

        if (criteria.$type === "And" || criteria.$type === "Or") {
            const results = [];
            for (let i = 0; i < criteria.criteria.length; i++) {
                results.push(this.remove(criteria.criteria[i], partialCriteria));
            }

            return (criteria.$type === "And" ? this.and : this.or).apply(this, results);
        }

        if (criteria.$type === 'Not') {
            criteria = this.remove(criteria.criteria, partialCriteria);
            if (!criteria) {
                return criteria;
            }

            return {
                '$type': 'Not',
                'criteria': criteria
            };
        }

        return matchesPartial(criteria, partialCriteria) ? null : criteria;
    }

    /**
     * 
     * @param criteria 
     * 
     * @returns 
     */
    flatten(criteria: Criteria): any[] {
        let results: Criteria[] = [];
        flatten(criteria, results, !!criteria && criteria.$type === "And");
        
        // flatten must be an array of criteria to apply AND, so, if top level was OR, we must keep OR
        if (results.length > 1 && criteria.$type == 'Or') { // TODO FIXME Is this enough?!
            results = [{
                '$type': 'Or',
                'criteria': results
            }];
        }
        
        return results;
    }

    /**
     * 
     * @param criteria 
     * @param partialCriteria 
     * 
     * @returns 
     */
    find(criteria: Criteria | null, partialCriteria: object|string): Criteria | null {
        if (!criteria) {
            return null;
        }

        // if type was specified as argument instead of partialCriteria
        if (typeof partialCriteria == "string") {
            partialCriteria = {"$type": partialCriteria};
        }

        if (matchesPartial(criteria, partialCriteria)) {
            return criteria;
        }

        if (criteria.$type === "Not") {
            return this.find(criteria.criteria, partialCriteria);
        }

        if (criteria.$type === "And" || criteria.$type === "Or") {
            for (let i = 0; i < criteria.criteria.length; i++) {
                const c = this.find(criteria.criteria[i], partialCriteria);
                if (c) {
                    return c;
                }
            }
        }

        return null;
    }

    /**
     * 
     * @param criteria 
     * @param partialCriteria 
     * 
     * @returns 
     */
    findAll(criteria: Criteria | null, partialCriteria: object): Criteria[] {
        if (!criteria) {
            return [];
        }

        if (criteria.$type === "And" || criteria.$type === "Or") {
            const result: Criteria[] = [];
            for (let i = 0; i < criteria.criteria.length; i++) {
                let c = this.find(criteria.criteria[i], partialCriteria);
                if (c) {
                    result.push(c);
                }
            }

            return result;
        } else {
            const result = this.find(criteria, partialCriteria);
            return result? [result] : [];
        }
    }

    /**
     * 
     * @param criteria 
     * @param feature 
     * 
     * @returns 
     */
    matchFeature(criteria: Criteria, feature): boolean {
        return this.matchers[criteria.$type](criteria, feature);
    }

    /**
     * Calculate the bounds for the criteria given (if it is a spatial criteria - For example, the bounds of an Interacts criteria
     * would be the bounds of the test geometry.)
     * @ return a jquery deferred resolved with the bounds for the criteria
     */
    calculateBounds(criteria: Criteria): JQueryPromise<number[] | null> {
        const dfds: JQueryDeferred<number[]>[] = [];
        this.calculateBoundsIntoDeferreds(criteria, dfds);
        return $.when.apply($, dfds).then((...args: Array<number[] | undefined>) => {
            let ret = null;
            for (let a = args.length; a-- > 0;) {
                const bounds = args[a];
                if (bounds) {
                    this.boundsUtil.sanitize(bounds);
                    if (ret) {
                        ret = this.boundsUtil.union(ret, bounds);
                    } else {
                        ret = bounds;
                    }
                }
            }
            
            return ret;
        });
    }

    /**
     * 
     * @param criteria 
     * @param dfds 
     * 
     * @returns 
     */
    private calculateBoundsIntoDeferreds(criteria: Criteria | null | undefined, dfds: JQueryPromise<number[]>[]) {
        if (!criteria) {
            return;
        }

        switch (criteria.$type) {
            case "WithinDistance":
                const dfd = this.calculateGeomCriteriaBounds(criteria as WithinDistanceCriteria).then((bounds) => {
                    return es.api.projection.get(4326).then((projection) => {
                        let buffer = this.distanceUnitUtil.toMeters(criteria.unit, criteria.distance) / projection.toMeters; // get the buffer amount in map units

                        //Apply a scale factor to this based on the position...
                        return Promise.all([
                            es.api.projection.calculateScaleFactorAt(projection, bounds[0], bounds[1]),
                            es.api.projection.calculateScaleFactorAt(projection, bounds[2], bounds[3])
                        ]).then(([scaleFactorA, scaleFactorB]) => {
                            const scaleFactor = Math.max(scaleFactorA, scaleFactorB);
                            buffer *= scaleFactor;

                            bounds = this.boundsUtil.bufferBy(bounds, buffer); // buffer bounds by scale factor
                            bounds = this.boundsUtil.intersection(projection.maxBounds, bounds);
                            
                            return bounds;
                        });
                    });
                });
                dfds.push(dfd);
                break;
            case "Interacts":
            case "Nearest":
                dfds.push(this.calculateGeomCriteriaBounds(criteria));
                break;
            // TODO: ide says this case is unreachable
            case "BoundsCriteria":
                dfds.push(es.util.deferred.resolved(criteria.bounds));
                break;
            case "And":
            case "Or":
                const criterias: Criteria[] = criteria.criteria;
                for (let c = criterias.length; c-- > 0;) {
                    this.calculateBoundsIntoDeferreds(criterias[c], dfds);
                }
                break;
            case "Not":
                this.calculateBoundsIntoDeferreds(criteria.criteria, dfds);
                break;
        }
    }

    /**
     * 
     * @param geomCriteria 
     * 
     * @returns 
     */
    private calculateGeomCriteriaBounds(geomCriteria: InteractsCriteria | NearestCriteria | WithinDistanceCriteria): JQueryPromise<number[]> {
        if (geomCriteria.geometry) {
            const bounds = es.api.geom.getBounds(geomCriteria.geometry);
            return es.util.deferred.resolved(bounds);
        }
        
        return es.api.geoShape.load(geomCriteria.geoShapeKey).then((geoShape) => {
            const bounds = es.api.geom.getBounds(geoShape.geom);
            return bounds;
        });
    }

    getAttrs(criteria?: Criteria, attrs?: string[]): string[] {
        if (!attrs) {
            attrs = [];
        }

        if (criteria) {
            switch(criteria.$type) {
                case "And":
                case "Or":
                    const criterias = criteria.criteria;
                    for (let c = criterias.length; c-- > 0;) {
                        this.getAttrs(criterias[c], attrs);
                    }
                    break;

                case "Not":
                    this.getAttrs(criteria.criteria, attrs);
                    break;

                case "TerritoryLevel":
                    attrs.push("ES_LEVEL");
                    if (criteria.parent) {
                        attrs.push("ES_PARENT_ID");
                    }
                    break;

                default:
                    if (criteria.attr && (attrs.indexOf(criteria.attr) < 0)) {
                        attrs.push(criteria.attr);
                    }
            }
        }

        return attrs;
    }
}

/**
 * 
 * @param criteria 
 * @param partialCriteria 
 * 
 * @returns 
 */
function matchesPartial(criteria: Criteria, partialCriteria: Record<string, any>): boolean {
    if (criteria === partialCriteria) {
        return true;
    }

    for (let k in partialCriteria) {
        if (criteria[k] !== partialCriteria[k]) {
            return false;
        }
    }

    return true;
}

/**
 * 
 * @param criteria 
 * @param results 
 * @param flattenAndMode true if flattening an And, false if flattening an Or
 */
function flatten(criteria: Criteria | null, results: Criteria[], flattenAndMode: boolean) {
    if (criteria) {
        if ((criteria.$type === "And" && flattenAndMode) || (criteria.$type === "Or" && !flattenAndMode)) {
            const criterias = criteria.criteria;
            for (let c = 0; c < criterias.length; c++) {
                flatten(criterias[c], results, flattenAndMode);
            }
        } else if ((criteria.$type === "And" && !flattenAndMode) || (criteria.$type === "Or" && flattenAndMode)) {
            const innerCriteria = criteria.criteria;
            const innerResults: Criteria[] = [];
            for (let c = 0; c < innerCriteria.length; c++) {
                flatten(innerCriteria[c], innerResults, !flattenAndMode);
            }

            if (innerResults.length == 1) {
                results.push(innerResults[0]); // only 1 criteria, no need to Or/And it
            } else if (innerResults.length > 1) {
                results.push({"$type": flattenAndMode ? "Or" : "And", "criteria": innerResults});
            } // else do nothing if 0
        } else if(criteria.$type !== "All") {
            // TODO: fix. this is causing {$type: "All"} to be removed which is not necessarily what you want
            results.push(criteria);
        }
    }
}

class CriteriaUtilMatchers {
    private criteriaUtil: CriteriaUtil;

    constructor(criteriaUtil: CriteriaUtil) {
        this.criteriaUtil = criteriaUtil;
    }

    /**
     * 
     * @param criteria 
     * @param feature 
     * 
     * @returns 
     */
    And(criteria: AndCriteria, feature): boolean {
        const criterias = criteria.criteria;
        for (let c = 0; c < criterias.length; c++) {
            const crit = criterias[c];

            if (!this.criteriaUtil.matchFeature(crit, feature)) {
                return false;
            }
        }

        return true;
    }

    /**
     * 
     * @param criteria 
     * @param feature 
     * 
     * @returns 
     */
    Or(criteria: OrCriteria, feature): boolean {
        const criterias = criteria.criteria;
        for (let c = 0; c < criterias.length; c++) {
            const crit = criterias[c];

            if (this.criteriaUtil.matchFeature(crit, feature)) {
                return true;
            }
        }

        return false;
    }

    /**
     * 
     * @param criteria 
     * @param feature 
     * 
     * @returns 
     */
    Not(criteria: NotCriteria, feature): boolean {
        return !this.criteriaUtil.matchFeature(criteria.criteria, feature);
    }

    /**
     * 
     * @returns 
     */
    All(): boolean {
        return true;
    }

    /**
     * 
     * @param criteria 
     * @param feature 
     * 
     * @returns 
     */
    Equal(criteria: EqualCriteria<any>, feature): boolean {
        let value = (criteria.attr === "ES_ID") ? feature.id : feature.attrs[criteria.attr];
        let criteriaValue = criteria.value;
        
        if (value === "" || value === undefined) {
            value = null;
        }

        if (criteriaValue === "" || criteriaValue === undefined) {
            criteriaValue = null;
        }
        
        if (value == null && criteriaValue == null) {
            return true;
        }
        
        if (value == null || criteriaValue == null) {
            return false;
        }
        
        if (typeof value === "number") {
            return Math.abs(criteriaValue - value) < 1e-9;
        }
        
        if (typeof value === "boolean") {
            return value == criteriaValue;
        }
        
        return value === criteriaValue;
    }

    /**
     * 
     * @param criteria 
     * @param feature 
     * 
     * @returns 
     */
    NotEqual(criteria: NotEqualCriteria<any>, feature): boolean {
        return !this.Equal(criteria, feature);
    }

    /**
     * 
     * @param criteria 
     * @param feature 
     * 
     * @returns 
     */
    Like(criteria: LikeCriteria, feature): boolean {
        let criteriaValue = criteria.value;
        if (criteriaValue) {
            criteriaValue = criteriaValue.toString().toLowerCase();
            let value = ((criteria.attr === "ES_ID") ? feature.id : feature.attrs[criteria.attr]);
            if (value) {
                value = value.toString().toLowerCase();
                return (value.indexOf(criteriaValue) >= 0);
            }
            return false;
        } else {
            return true;
        }
    }

    /**
     * 
     * @param criteria 
     * @param feature 
     * 
     * @returns 
     */
    Greater(criteria: GreaterCriteria, feature): boolean {
        const value = (criteria.attr === "ES_ID") ? feature.id : feature.attrs[criteria.attr];
        if (value == null) {
            return false;
        }

        if (typeof value === "number"){
            return value > criteria.value;
        }

        //Should not happen
        return true;
    }

    /**
     * 
     * @param criteria 
     * @param feature 
     * 
     * @returns 
     */
    GreaterEqual(criteria: GreaterEqualCriteria, feature): boolean {
        const value = (criteria.attr === "ES_ID") ? feature.id : feature.attrs[criteria.attr];
        if (value == null) {
            return false;
        }

        if (typeof value === "number"){
            return value >= criteria.value;
        }

        //Should not happen
        return true;
    }

    /**
     * 
     * @param criteria 
     * @param feature 
     * 
     * @returns 
     */
    Less(criteria: LessCriteria, feature): boolean {
        const value = (criteria.attr === "ES_ID") ? feature.id : feature.attrs[criteria.attr];
        if (value == null) {
            return false;
        }

        if (typeof value === "number") {
            return value < criteria.value;
        }

        //Should not happen
        return true;
    }

    /**
     * 
     * @param criteria 
     * @param feature 
     * 
     * @returns 
     */
    LessEqual(criteria: LessEqualCriteria, feature): boolean {
        const value = (criteria.attr === "ES_ID") ? feature.id : feature.attrs[criteria.attr];
        if (value == null) {
            return false;
        }

        if (typeof value === "number") {
            return value <= criteria.value;
        }

        //Should not happen
        return true;
    }

    /**
     * 
     * @param criteria 
     * @param feature 
     * 
     * @returns 
     */
    DateGreater(criteria: DateGreaterCriteria, feature): boolean {
        let value = feature.attrs[criteria.attr];

        if (value == null) {
            return false;
        }
        
        value = value.getTime();

        if (typeof value === "number"){
            return value > criteria.value;
        }

        //Should not happen, should this throw exception?
        return true;
    }

    /**
     * 
     * @param criteria 
     * @param feature 
     * 
     * @returns 
     */
    DateGreaterEqual(criteria: DateGreaterEqualCriteria, feature): boolean {
        let value = feature.attrs[criteria.attr];

        if (value == null) {
            return false;
        }
        
        value = value.getTime();

        if (typeof value === "number"){
            return value >= criteria.value;
        }

        //Should not happen, should this throw exception?
        return true;
    }

    /**
     * 
     * @param criteria 
     * @param feature 
     * 
     * @returns 
     */
    DateLessEqual(criteria: DateLessEqualCriteria, feature): boolean {
        let value = feature.attrs[criteria.attr];

        if (value == null) {
            return false;
        }
        
        value = value.getTime();

        if (typeof value === "number"){
            return value <= criteria.value;
        }

        //Should not happen, should this throw exception?
        return true;
    }

    /**
     * 
     * @param criteria 
     * @param feature 
     * 
     * @returns 
     */
    In(criteria: InCriteria<any>, feature): boolean {
        let value = (criteria.attr === "ES_ID") ? feature.id : feature.attrs[criteria.attr];
        const values = criteria.values;
        if (typeof value === "number") {
            for (let v = 0; v < values.length; v++) {
                if (Math.abs(values[v] - value) < 1e-9) {
                    return true;
                }
            }
        } else if (typeof value === "boolean") {
            for (let v = 0; v < values.length; v++) {
                if (value == values[v]) {
                    return true;
                }
            }
        } else {
            if (value === "") {
                value = null;
            }

            for (let v = 0; v < values.length; v++) {
                let criteriaValue = values[v];

                if (criteriaValue === "" || criteriaValue == undefined) {
                    criteriaValue = null;
                }

                if (value === criteriaValue) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * 
     * @param criteria 
     * @param feature 
     * 
     * @returns 
     */
    WithinDistance(criteria: WithinDistanceCriteria, feature): boolean {
        //console.warn("WithinDistance vector mode. returning true", criteria, feature);
        return true; // FIXME TODO TODOIMPL
    }

    /**
     * 
     * @param criteria 
     * @param feature 
     * 
     * @returns 
     */
    Nearest(criteria: NearestCriteria, feature): boolean {
        //console.warn("Nearest vector mode. returning true", criteria, feature);
        return true; // FIXME TODO TODOIMPL
    }

    /**
     * 
     * @param criteria 
     * @param feature 
     * 
     * @returns 
     */
    Interacts(criteria: InteractsCriteria, feature): boolean {
        //console.warn("Interacts vector mode. returning true", criteria, feature);
        return true; // FIXME TODO TODOIMPL
    }

    /**
     * 
     * @param criteria 
     * @param feature 
     * 
     * @returns 
     */
    TerritoryLevel(criteria: TerritoryLevelCriteria, feature): boolean {
        if (criteria.level != feature.attrs.ES_LEVEL) {
            return false;
        }

        if (!criteria.parent) {
            return true;
        }

        return criteria.parent == feature.attrs['ES_PARENT_ID'];
    }
}
